import { useEffect, useRef, useState, useCallback } from 'react';
import { formatRelative } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useClient } from '../api/clients';
import { TimePeriod } from '../api/aboboApiTypes';
import { getTimezoneAbbreviation } from '../timezone';
import dateFnsLocales from "../dateFnsLocales";

const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;


interface UseClientBusinessHoursReturn {
  isClientOpen: boolean;
  nextOpenAt: Date | null;
  getFormattedNextOpenTime: () => string | null;
  isLoading: boolean;
  isError: boolean;
}

export const useClientBusinessHours = (): UseClientBusinessHoursReturn => {
  const { client, isLoading, isError } = useClient();
  const { i18n } = useTranslation();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  const [isClientOpen, setIsClientOpen] = useState<boolean>(true);
  const [nextOpenAt, setNextOpenAt] = useState<Date | null>(null);

  const calculateBusinessHours = useCallback(() => {
    if (!client?.hoursOfOperation || !client?.timeZone) {
      setIsClientOpen(true); // If no hours set, assume always open
      setNextOpenAt(null);
      return;
    }

    const now = new Date();
    
    // Get current time in clinic's timezone
    const nowInClinicTz = new Date(now.toLocaleString("en-US", { timeZone: client.timeZone }));
    const currentTime = nowInClinicTz.getHours() * 60 + nowInClinicTz.getMinutes();
    const dayName = DAYS_OF_WEEK[nowInClinicTz.getDay()];
    
    // Get today's periods
    const todaysPeriods = client.hoursOfOperation[dayName] || [];
    
    // Check if current time falls within any of today's periods
    let isOpen = false;
    for (const period of todaysPeriods) {
      if (timeInPeriod(currentTime, period)) {
        isOpen = true;
        break;
      }
    }

    // Check yesterday's periods for overnight hours
    if (!isOpen) {
      const yesterday = new Date(nowInClinicTz);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayName = DAYS_OF_WEEK[yesterday.getDay()];
      const yesterdayPeriods = client.hoursOfOperation[yesterdayName] || [];

      for (const period of yesterdayPeriods) {
        const openMinutes = timeToMinutes(period.open);
        const closeMinutes = timeToMinutes(period.close);
        
        // Only check overnight periods (close < open)
        if (closeMinutes < openMinutes && currentTime < closeMinutes) {
          isOpen = true;
          break;
        }
      }
    }

    setIsClientOpen(isOpen);

    // Calculate next opening time if closed
    let nextOpen: Date | null = null;
    if (!isOpen) {
      // Check the next 7 days for the next opening time
      for (let daysAhead = 0; daysAhead < 7; daysAhead++) {
        const checkDate = new Date(nowInClinicTz);
        checkDate.setDate(checkDate.getDate() + daysAhead);
        
        const dayName = DAYS_OF_WEEK[checkDate.getDay()];
        const periods = client.hoursOfOperation[dayName] || [];

        for (const period of periods) {
          const openTime = parseTimeOnDate(checkDate, period.open, client.timeZone);
          
          // For today (daysAhead === 0), only return times in the future
          if (daysAhead === 0 && openTime <= nowInClinicTz) {
            continue;
          }
          
          nextOpen = openTime;
          break;
        }
        
        if (nextOpen) break;
      }
    }

    setNextOpenAt(nextOpen);
  }, [client?.hoursOfOperation, client?.timeZone]);

  const getFormattedNextOpenTime = useCallback((): string | null => {
    if (!nextOpenAt || !client?.timeZone) return null;
    
    try {
      // Since nextOpenAt is calculated in clinic timezone, we need to format it correctly
      // Create a date in the clinic's timezone for display
      const timeString = nextOpenAt.toLocaleTimeString('en-US', {
        timeZone: client.timeZone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      // Format relative time using the actual nextOpenAt date
      const relativeTime = formatRelative(nextOpenAt, new Date(), { locale: dateFnsLocales[i18n.language] });
      
      // Get timezone abbreviation for the specific date (always English)
      const timezoneAbbr = getTimezoneAbbreviation(client.timeZone, nextOpenAt);
      
      // Combine relative time with timezone - replace the time part with clinic timezone time
      const relativeParts = relativeTime.split(' at ');
      if (relativeParts.length === 2) {
        return `${relativeParts[0]} at ${timeString} (${timezoneAbbr})`;
      }
      
      return `${relativeTime} (${timezoneAbbr})`;
    } catch (error) {
      console.warn('Error formatting next open time:', error);
      return null;
    }
  }, [nextOpenAt, client?.timeZone, i18n.language]);

  // Calculate initial values and set up interval
  useEffect(() => {
    calculateBusinessHours();

    // Set up interval to refresh every 5 minutes
    intervalRef.current = setInterval(calculateBusinessHours, 5 * 60 * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [calculateBusinessHours]);

  return {
    isClientOpen,
    nextOpenAt,
    getFormattedNextOpenTime,
    isLoading,
    isError,
  };
};

// Helper functions
function timeToMinutes(timeString: string): number {
  if (!timeString) return 0;
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
}

function timeInPeriod(currentTimeMinutes: number, period: TimePeriod): boolean {
  if (!period.open || !period.close) return false;

  const openMinutes = timeToMinutes(period.open);
  const closeMinutes = timeToMinutes(period.close);

  if (closeMinutes > openMinutes) {
    // Normal hours (same day)
    return currentTimeMinutes >= openMinutes && currentTimeMinutes < closeMinutes;
  } else {
    // Overnight hours (spans two days)
    return currentTimeMinutes >= openMinutes || currentTimeMinutes < closeMinutes;
  }
}

function parseTimeOnDate(date: Date, timeString: string, timeZone?: string): Date {
  if (!timeString) return new Date(date);
  
  const [hours, minutes] = timeString.split(':').map(Number);
  
  if (timeZone) {
    // Create an ISO string for the date and time in the clinic's timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hourStr = String(hours).padStart(2, '0');
    const minuteStr = String(minutes).padStart(2, '0');
    
    // Create a date string that represents the clinic time
    const isoString = `${year}-${month}-${day}T${hourStr}:${minuteStr}:00`;
    
    // Parse as if it's in the clinic timezone and get the UTC equivalent
    const tempDate = new Date(isoString);
    const utcTime = tempDate.getTime();
    
    // Get the timezone offset for this specific date in the clinic's timezone
    const testDate = new Date(utcTime);
    const offsetMs = testDate.getTime() - new Date(testDate.toLocaleString('sv-SE', { timeZone })).getTime();
    
    return new Date(utcTime + offsetMs);
  }
  
  // Fallback to original behavior
  const result = new Date(date);
  result.setHours(hours, minutes, 0, 0);
  return result;
}